import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wallet, GitCompare, ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { ASSETS } from "../assets";
import { getTopWalletI18n } from "../i18n/topwallet";
import { TOPWALLET_SVG_BY_ID } from "./topwallet/TopWalletCardSvgs";

export const TopWalletSection = () => {
  const { t, lang } = useLanguage();
  const tw = getTopWalletI18n(lang);

  return (
    <section
      id="topwallet"
      className="relative w-full overflow-hidden bg-black py-24 md:py-32 lg:py-40"
      data-testid="topwallet-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.18),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(0,229,255,0.10),transparent_55%)]" />
      <div className="absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-blue-400/25 bg-blue-400/5 px-3 py-1.5 backdrop-blur"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-200 md:text-xs">
              {t.topwallet.eyebrow}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 flex items-center gap-5"
          >
            <img
              src={ASSETS.logos.topwallet}
              alt="TopWallet"
              className="h-20 w-20 rounded-2xl object-cover shadow-[0_0_50px_rgba(59,130,246,0.5)] ring-1 ring-white/10 md:h-24 md:w-24"
              data-testid="topwallet-logo"
            />
            <div>
              <h2 className="font-display text-4xl font-black tracking-tighter md:text-6xl">
                <span
                  style={{
                    backgroundImage: "linear-gradient(90deg, #ffffff 0%, #fff5e6 35%, #f5a623 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  TOPWALLET
                </span>
              </h2>
              <p
                className="mt-1 text-lg font-semibold md:text-xl"
                style={{ color: "#f5a623" }}
              >
                {t.topwallet.tagline}
              </p>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-white/55 md:text-lg"
          >
            {t.topwallet.description}
          </motion.p>
        </div>

        {/* 4 cards in a uniform 2×2 grid (all same size) + 5th comparator card */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {tw.cards.map((c, i) => {
            const Illustration = TOPWALLET_SVG_BY_ID[c.id];
            return (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.1 * i }}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl transition-all hover:border-blue-400/30 hover:shadow-[0_0_40px_rgba(59,130,246,0.2)]"
                data-testid={`topwallet-card-${c.id}`}
              >
                {/* Illustration (clean — no overlays) */}
                <div className="relative aspect-[540/340] w-full overflow-hidden">
                  {Illustration ? <Illustration /> : <Wallet className="m-auto h-20 w-20 text-cyan-300" />}
                </div>

                {/* Body */}
                <div className="p-6 md:p-7">
                  <h3 className="flex items-center gap-2 font-display text-xl font-bold text-white md:text-2xl">
                    <span className="text-2xl">{c.icono}</span>
                    {c.titulo}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55 md:text-base">
                    {c.descripcion}
                  </p>

                  <Link
                    to={c.href}
                    data-testid={`topwallet-cta-${c.id}`}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg text-[13px] font-bold uppercase tracking-[0.12em] text-[#07080f] shadow-[0_4px_16px_rgba(245,166,35,0.3)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(245,166,35,0.5)]"
                    style={{
                      background: "linear-gradient(135deg, #f5a623, #e8941a)",
                      padding: "13px 20px",
                    }}
                  >
                    {c.boton}
                  </Link>
                </div>
              </motion.article>
            );
          })}

          {/* 5th card · Comparador de 35 Wallets — centered, same size as the others */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="group relative overflow-hidden rounded-3xl border border-amber-400/20 bg-white/[0.03] backdrop-blur-xl transition-all hover:border-amber-400/50 hover:shadow-[0_0_40px_rgba(245,166,35,0.25)] md:col-span-2 md:mx-auto md:w-[calc(50%-12px)]"
            data-testid="topwallet-card-comparador"
          >
            <div className="relative aspect-[540/340] w-full overflow-hidden">
              <picture>
                <source srcSet="/topwallet/comparador.webp" type="image/webp" />
                <img
                  src="/topwallet/comparador.png"
                  alt="REBEL TopWallet — Comparador de Wallets"
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                />
              </picture>
            </div>
            <div className="p-6 md:p-7">
              <h3 className="flex items-center gap-2 font-display text-xl font-bold text-white md:text-2xl">
                <span className="text-2xl">⚖</span>
                {tw.comparadorCard.titulo}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55 md:text-base">
                {tw.comparadorCard.descripcion}
              </p>

              <Link
                to="/topwallet/comparador"
                data-testid="topwallet-comparador-cta"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg text-[13px] font-bold uppercase tracking-[0.12em] text-[#07080f] shadow-[0_4px_16px_rgba(245,166,35,0.3)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(245,166,35,0.5)]"
                style={{
                  background: "linear-gradient(135deg, #f5a623, #e8941a)",
                  padding: "13px 20px",
                }}
              >
                <GitCompare className="h-4 w-4" />
                {tw.comparadorBtn}
              </Link>
            </div>
          </motion.article>
        </div>

        {/* Secondary CTA: aggregated articles hub */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, delay: 0.15 }}
          className="mt-10 flex justify-center"
        >
          <Link
            to="/topwallet/articulos"
            data-testid="topwallet-articulos-cta"
            className="group inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-200 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-cyan-300/60 hover:bg-cyan-400/10 hover:shadow-[0_4px_20px_rgba(59,130,246,0.25)]"
          >
            <span>{tw.todosArticulos}</span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
